<script lang="ts" setup>
import Container from '@/components/Container.vue';
import { LANDING_CONSULTATION_PRICE } from '@/landing/landingConsultationPrice.js';
import { formatMoney } from '@nzyme/money/formatMoney.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';

import * as l from './FaqSection.loc.js';

const t = useTranslate();
</script>

<template>
  <Container
    id="faq"
    tag="section"
    :class="css.faqSection"
    aria-labelledby="faq-title"
  >
    <div :class="css.sectionTitle">
      <h2 id="faq-title">{{ t(l.faqTitle) }}</h2>
    </div>
    <ul :class="[css.faqItems, css.borderPatientGradientContainer, css.animated]">
      <li :class="[css.faqItem, css.borderPatientGradientItem, css.animated]">
        <h3 :class="css.faqItem_title">{{ t(l.faqWhatIsHealedTitle) }}</h3>
        <p :class="css.faqItem_description">{{ t(l.faqWhatIsHealedDescription) }}</p>
      </li>
      <li :class="[css.faqItem, css.borderPatientGradientItem, css.animated]">
        <h3 :class="css.faqItem_title">{{ t(l.faqHowLongWaitTitle) }}</h3>
        <p :class="css.faqItem_description">{{ t(l.faqHowLongWaitDescription) }}</p>
      </li>
      <li :class="[css.faqItem, css.borderPatientGradientItem, css.animated]">
        <h3 :class="css.faqItem_title">{{ t(l.faqWillIGetPrescriptionTitle) }}</h3>
        <p :class="css.faqItem_description">{{ t(l.faqWillIGetPrescriptionDescription) }}</p>
      </li>
      <li :class="[css.faqItem, css.borderPatientGradientItem, css.animated]">
        <h3 :class="css.faqItem_title">{{ t(l.faqAnyMedicineTitle) }}</h3>
        <p :class="css.faqItem_description">{{ t(l.faqAnyMedicineDescription) }}</p>
      </li>
      <li :class="[css.faqItem, css.borderPatientGradientItem, css.animated]">
        <h3 :class="css.faqItem_title">{{ t(l.faqEmergencyTitle) }}</h3>
        <p :class="css.faqItem_description">{{ t(l.faqEmergencyDescription) }}</p>
      </li>
      <li :class="[css.faqItem, css.borderPatientGradientItem, css.animated]">
        <h3 :class="css.faqItem_title">{{ t(l.faqCostTitle) }}</h3>
        <p :class="css.faqItem_description">
          {{ t(l.faqCostDescription, { price: formatMoney(LANDING_CONSULTATION_PRICE, { withCurrency: 'code' }) }) }}
        </p>
      </li>
    </ul>
  </Container>
</template>

<style lang="scss" module="css">
.faqSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-medium);
  background: $opacity-white-30;
  padding-block: var(--space-large);

  // Flow spacing for child elements
  > * + * {
    margin-block-start: clamp(0.75rem, 1.5vw, 1.25rem);
  }
}

.sectionTitle {
  text-align: center;
  margin: 0;

  h2 {
    margin: 0;
    font-size: var(--font-size-heading-2);
    font-weight: $font-weight-600;
    line-height: $line-height-heading-2;
    letter-spacing: $letter-spacing-heading-2;
  }
}

.faqItems {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.faqItem {
  margin: 0;
  padding: var(--space-xsmall);

  @media screen and (min-width: 1280px) {
    padding: var(--space-small);
  }
}

.faqItem_title {
  font-size: var(--font-size-text-medium);
  margin: 0 0 1rem 0;
  font-weight: $font-weight-600;
}

.faqItem_description {
  margin: 0;
  color: $color-gray-700;
}

.borderPatientGradientContainer {
  border-style: solid;
  border-width: 1px;
  border-color: $color-patient-200;
  border-image: linear-gradient(135deg, $color-patient-200 30%, $color-doctor-200 70%);
  border-image-slice: 1;
  border-image-width: 1;
  border-image-outset: 0;
}

.borderPatientGradientItem {
  border-style: solid;
  border-color: $color-patient-200;
  border-image: linear-gradient(135deg, $color-patient-200 30%, $color-doctor-200 70%);
  border-image-slice: 1;
  border-image-width: 1;
  border-image-outset: 0;

  // Mobile: bottom border only
  border-width: 0 0 1px 0;

  &:last-of-type {
    border-width: 0;
  }

  @media screen and (min-width: 768px) {
    &:first-of-type {
      border-width: 0 1px 1px 0;
    }

    &:nth-of-type(odd) {
      border-width: 0 1px 1px 0;
    }

    &:nth-of-type(n + 5) {
      border-width: 0 1px 0 0;
    }

    &:last-of-type {
      border-width: 0;
    }
  }

  @media screen and (min-width: 1024px) {
    &:first-of-type {
      border-width: 0 1px 1px 0;
      animation-delay: 4s;
    }

    &:nth-of-type(n + 2) {
      border-width: 0 1px 1px 0;
    }

    &:nth-of-type(n + 3) {
      border-width: 0 0 1px 0;
    }

    &:nth-of-type(n + 4) {
      border-width: 0 1px 0 0;
    }

    &:last-of-type {
      border-width: 0;
    }
  }
}

.animated {
  animation: gradient-rotate 10s $transition-ease-out-quad infinite;
}
</style>
